import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { ComponentType } from "@constants/ComponentType";

import { ALLink } from "@components/ALComponents";
import { CHAR_COUNT_OPTION_NAME } from "@components/ProductPage/mto/mtoVariants";

import { isBrowser } from "@helpers/browser";
import { cn } from "@helpers/general";

const MTO_FONT_MAPPING = {
  MODERN: "MiniBlock",
  modern: "MiniBlock-Lower",
  Signature: "Sloop",
  Script: "MiniScript",
};

// The value a param holds on the page being left, or `null` (server side included, where the link
// is rendered without a query string and filled in on hydration).
const getCurrentParam = (param) =>
  (isBrowser ? new URLSearchParams(window.location.search).get(param) : null) || null;

// Carries a param onto the alternative, dropping it when there is nothing left to carry (the PDP
// ads pages start from the whole query string, which can already hold a stale one)
const setParam = (queryParams, param, value) => {
  if (value) {
    queryParams.set(param, value);
  } else {
    queryParams.delete(param);
  }
};

function ProductAlternativeTextBlockProduct({
  handle,
  isActive,
  labelName,
  labelTitle,
  fullWidth,
  componentType,
  isMostPopular,
  previewValue = null,
  children,
}) {
  const [link, setLink] = useState(`/products/${handle}/`);

  // The character count belongs to the customization too, and the variant selector writes it to the
  // URL from its own change handler, so it is never a render behind there. It is read while
  // rendering rather than inside the effect so that picking another variant rebuilds the link.
  const charCount = getCurrentParam(CHAR_COUNT_OPTION_NAME);

  useEffect(() => {
    let base = `/products/${handle}/`;
    if (componentType === ComponentType.PDP_ADS) {
      base = `/p/${handle}/`;
    }

    const queryParams = new URLSearchParams(
      componentType === ComponentType.PDP_ADS && isBrowser ? window.location.search : ""
    );

    // A customizable product owns its own preview: every layout mirrors it into the URL from an
    // effect of its own, which runs after this one (the alternatives are rendered first), so reading
    // it back from there would leave the link a character behind what is on screen. Only a product
    // with no customization at all falls back on the URL.
    const preview = previewValue ?? getCurrentParam("preview");

    setParam(queryParams, "preview", preview);
    setParam(queryParams, CHAR_COUNT_OPTION_NAME, charCount);

    const queryString = queryParams.toString();
    setLink(queryString ? `${base}?${queryString}` : base);
  }, [componentType, handle, previewValue, charCount]);

  return (
    <span
      key={`alternative-${handle}-${labelName}`}
      className={`${cn(
        "relative flex items-center justify-center flex-grow max-w-fit min-w-[calc((100%-20px)/3)] border border-[#4a4a4a] rounded-[4px] cursor-pointer",
        "h-[35px] mobile:h-[28px]",
        "bg-white hover:bg-black hover:text-white",
        labelTitle === "Font" ? "min-w-[calc((100%-20px)/2)]" : "min-w-[calc((100%-20px)/3)]",
        fullWidth && "max-w-none",
        isActive && "bg-black text-white"
      )}`}
    >
      {isMostPopular && (
        <span
          className={cn(
            "absolute -top-[27px] mobile:-top-[24px] left-1/2 -translate-x-1/2 px-[7px] py-[3px] rounded-[4px]",
            "bg-light-grey text-black typo-micro font-bold"
          )}
        >
          Most&nbsp;Popular
        </span>
      )}
      <ALLink
        className={cn(
          "w-full h-full flex items-center justify-center",
          "typo-body font-bold",
          labelTitle === "Font" && "text-[18px] mobile:text-[16px] tracking-[1px]",
          labelTitle === "Font" && (labelName === "MODERN" || labelName === "Script") && "pt-[2px]"
        )}
        to={link}
        title={labelName || ""}
        style={MTO_FONT_MAPPING[labelName] && { fontFamily: MTO_FONT_MAPPING[labelName] }}
      >
        {children}
      </ALLink>
    </span>
  );
}

ProductAlternativeTextBlockProduct.propTypes = {
  handle: PropTypes.string,
  isActive: PropTypes.bool,
  labelName: PropTypes.string,
  labelTitle: PropTypes.string,
  fullWidth: PropTypes.bool,
  componentType: PropTypes.string,
  isMostPopular: PropTypes.bool,
  previewValue: PropTypes.string,
  children: PropTypes.node,
};

export default ProductAlternativeTextBlockProduct;
